import { apiFetch } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { hasOperationalPresentation } from "./types";
import type {
  OrderSummaryFilters,
  AddItemToOrderPayload,
  UpdateOrderItemPayload,
  SetOrderItemBonusPayload,
  ReplaceOrderItemPayload,
  LegacyKgMigrationPreview,
  LegacyKgMigrationPayload,
  CreateChangeRequestPayload,
  RejectChangeRequestPayload,
  ChangeRequestResponse,
  ChangeRequestItem,
  EnrichedOrderItem,
  EnrichedOrderItemResponse,
  EnrichedOrderPageResponse,
  EnrichedOrderResponse,
  OrderAuditEntryPageResponse,
  OrderOverviewResponse,
  RemoveOrderItemResponse,
  OrderResponse,
  OrderSummaryPageResponse,
} from "./types";

const OPERATIONAL_FORBIDDEN_KEY_FRAGMENTS = [
  "price",
  "total",
  "value",
  "amount",
  "margin",
  "payment",
  "billing",
  "finance",
  "financial",
  "cents",
  "commission",
  "cost",
  "revenue",
  "discount",
  "charged",
  "computed",
  "realprice",
  "finalprice",
] as const;

type JsonRecord = Record<string, unknown>;
type ValueGuard = (value: unknown) => boolean;
type ResponseValidator = (value: unknown, path: string) => void;
type RequiredField = readonly [
  key: string,
  expected: string,
  guard: ValueGuard,
];

const isString = (value: unknown): value is string => typeof value === "string";
const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);
const isBoolean = (value: unknown): value is boolean =>
  typeof value === "boolean";
const isStringOrNull = (value: unknown): value is string | null =>
  value === null || isString(value);
const isNumberOrNull = (value: unknown): value is number | null =>
  value === null || isNumber(value);

function invalidResponse(
  responseName: string,
  path: string,
  expected: string,
): never {
  throw new TypeError(
    `Invalid ${responseName} at ${path}: expected ${expected}`,
  );
}

function asRecord(
  value: unknown,
  responseName: string,
  path: string,
): JsonRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return invalidResponse(responseName, path, "an object");
  }
  return value as JsonRecord;
}

function assertFields(
  record: JsonRecord,
  responseName: string,
  path: string,
  fields: readonly RequiredField[],
): void {
  for (const [key, expected, guard] of fields) {
    if (!guard(record[key])) {
      invalidResponse(responseName, `${path}.${key}`, expected);
    }
  }
}

function assertOptionalField(
  record: JsonRecord,
  responseName: string,
  path: string,
  key: string,
  expected: string,
  guard: ValueGuard,
): void {
  if (key in record && record[key] !== undefined && !guard(record[key])) {
    invalidResponse(responseName, `${path}.${key}`, expected);
  }
}

function assertArray(
  value: unknown,
  responseName: string,
  path: string,
  itemValidator: ResponseValidator,
): void {
  if (!Array.isArray(value)) {
    invalidResponse(responseName, path, "an array");
  }
  value.forEach((item, index) => itemValidator(item, `${path}[${index}]`));
}

function assertOperationalPresentation(
  value: unknown,
  responseName: string,
  path: string,
): JsonRecord {
  const record = asRecord(value, responseName, path);
  if (!hasOperationalPresentation(record)) {
    invalidResponse(responseName, `${path}.presentation`, '"operational"');
  }
  return record;
}

function assertNoPresentation(
  record: JsonRecord,
  responseName: string,
  path: string,
): void {
  if ("presentation" in record) {
    invalidResponse(
      responseName,
      `${path}.presentation`,
      "no presentation marker",
    );
  }
}

function assertNoFinancialKeysInOperationalPayload(
  value: unknown,
  path = "$",
  seen = new WeakSet<object>(),
): void {
  if (typeof value !== "object" || value === null) return;
  if (seen.has(value)) return;
  seen.add(value);

  for (const [key, nestedValue] of Object.entries(value)) {
    const normalizedKey = key.toLowerCase();
    if (
      OPERATIONAL_FORBIDDEN_KEY_FRAGMENTS.some((fragment) =>
        normalizedKey.includes(fragment),
      )
    ) {
      throw new TypeError(
        `Operational order response contains forbidden financial key at ${path}.${key}`,
      );
    }
    assertNoFinancialKeysInOperationalPayload(
      nestedValue,
      `${path}.${key}`,
      seen,
    );
  }
}

const FINANCIAL_ORDER_ITEM_FIELDS: readonly RequiredField[] = [
  ["id", "a string", isString],
  ["order_id", "a string", isString],
  ["quotation_item_id", "a string", isString],
  ["quantity", "a finite number", isNumber],
  ["real_price", "a finite number", isNumber],
  ["final_price", "a finite number", isNumber],
  ["is_available", "a boolean", isBoolean],
  ["return_status", "a string", isString],
  ["created_at", "a string", isString],
  ["created_by", "a string", isString],
  ["updated_at", "a string", isString],
  ["updated_by", "a string", isString],
];

const OPERATIONAL_ORDER_ITEM_FIELDS: readonly RequiredField[] = [
  ["id", "a string", isString],
  ["order_id", "a string", isString],
  ["quotation_item_id", "a string", isString],
  ["quantity", "a finite number", isNumber],
  ["bonus_quantity", "a finite number", isNumber],
  ["is_available", "a boolean", isBoolean],
  ["return_status", "a string", isString],
  ["created_at", "a string", isString],
  ["updated_at", "a string", isString],
];

function assertFinancialOrderItem(value: unknown, path: string): void {
  const responseName = "financial order item response";
  const record = asRecord(value, responseName, path);
  assertNoPresentation(record, responseName, path);
  assertFields(record, responseName, path, FINANCIAL_ORDER_ITEM_FIELDS);
  assertOptionalField(
    record,
    responseName,
    path,
    "direct_kg_quantity_mode",
    "a string or null",
    isStringOrNull,
  );
  assertOptionalField(
    record,
    responseName,
    path,
    "bonus_quantity",
    "a finite number or null",
    isNumberOrNull,
  );
}

function assertOperationalOrderItem(value: unknown, path: string): void {
  const responseName = "operational order item response";
  const record = assertOperationalPresentation(value, responseName, path);
  assertFields(record, responseName, path, OPERATIONAL_ORDER_ITEM_FIELDS);
  assertOptionalField(
    record,
    responseName,
    path,
    "direct_kg_quantity_mode",
    "a string or null",
    isStringOrNull,
  );
  assertOptionalField(
    record,
    responseName,
    path,
    "confirmed_quantity",
    "a finite number or null",
    isNumberOrNull,
  );
}

function assertFinancialEnrichedOrderItem(value: unknown, path: string): void {
  const responseName = "financial enriched order item response";
  const record = asRecord(value, responseName, path);
  assertFinancialOrderItem(record, path);
  assertFields(record, responseName, path, [
    ["product_id", "a string", isString],
    ["product_name", "a string", isString],
    ["supplier_id", "a string", isString],
    ["supplier_name", "a string", isString],
    ["price_per_unit", "a finite number", isNumber],
    ["computed_price", "a finite number", isNumber],
    ["charged_price", "a finite number", isNumber],
    ["unit_type", "a string", isString],
  ]);
  assertOptionalField(
    record,
    responseName,
    path,
    "price_per_box",
    "a finite number",
    isNumber,
  );
  assertOptionalField(
    record,
    responseName,
    path,
    "received_quantity",
    "a finite number or null",
    isNumberOrNull,
  );
  assertOptionalField(
    record,
    responseName,
    path,
    "price_per_kg",
    "a finite number",
    isNumber,
  );
}

function assertOperationalEnrichedOrderItem(
  value: unknown,
  path: string,
): void {
  const responseName = "operational enriched order item response";
  const record = assertOperationalPresentation(value, responseName, path);
  assertFields(record, responseName, path, [
    ...OPERATIONAL_ORDER_ITEM_FIELDS,
    ["product_name", "a string", isString],
    ["supplier_name", "a string", isString],
    ["unit_type", "a string", isString],
  ]);
  assertOptionalField(
    record,
    responseName,
    path,
    "direct_kg_quantity_mode",
    "a string or null",
    isStringOrNull,
  );
  assertOptionalField(
    record,
    responseName,
    path,
    "confirmed_quantity",
    "a finite number or null",
    isNumberOrNull,
  );
}

function assertFinancialOrder(value: unknown, path: string): void {
  const responseName = "financial order response";
  const record = asRecord(value, responseName, path);
  assertNoPresentation(record, responseName, path);
  assertFields(record, responseName, path, [
    ["id", "a string", isString],
    ["client_id", "a string", isString],
    ["store_id", "a string", isString],
    ["status", "a string", isString],
    ["version", "a finite number", isNumber],
    ["created_at", "a string", isString],
    ["created_by", "a string", isString],
    ["updated_at", "a string", isString],
    ["updated_by", "a string", isString],
  ]);
  assertOptionalField(
    record,
    responseName,
    path,
    "quotation_id",
    "a string or null",
    isStringOrNull,
  );
  if ("items" in record && record.items !== undefined) {
    assertArray(
      record.items,
      responseName,
      `${path}.items`,
      assertFinancialOrderItem,
    );
  }
}

function assertOperationalOrder(value: unknown, path: string): void {
  const responseName = "operational order response";
  const record = assertOperationalPresentation(value, responseName, path);
  assertFields(record, responseName, path, [
    ["id", "a string", isString],
    ["client_id", "a string", isString],
    ["store_id", "a string", isString],
    ["status", "a string", isString],
    ["version", "a finite number", isNumber],
    ["created_at", "a string", isString],
    ["updated_at", "a string", isString],
  ]);
  assertOptionalField(
    record,
    responseName,
    path,
    "quotation_id",
    "a string or null",
    isStringOrNull,
  );
  if ("items" in record && record.items !== undefined) {
    assertArray(
      record.items,
      responseName,
      `${path}.items`,
      assertOperationalOrderItem,
    );
  }
}

function assertFinancialLedgerMeta(value: unknown, path: string): JsonRecord {
  const responseName = "financial order response";
  const record = asRecord(value, responseName, path);
  assertNoPresentation(record, responseName, path);
  assertFields(record, responseName, path, [
    ["id", "a string", isString],
    ["client_id", "a string", isString],
    ["store_id", "a string", isString],
    ["store_name", "a string", isString],
    ["client_name", "a string", isString],
    ["quotation_id", "a string", isString],
    ["quotation_date", "a string", isString],
    ["status", "a string", isString],
    ["version", "a finite number", isNumber],
    ["created_at", "a string", isString],
    ["created_by", "a string", isString],
    ["updated_at", "a string", isString],
    ["updated_by", "a string", isString],
  ]);
  assertOptionalField(
    record,
    responseName,
    path,
    "checkin_id",
    "a string or null",
    isStringOrNull,
  );
  assertOptionalField(
    record,
    responseName,
    path,
    "pending_change_request_id",
    "a string",
    isString,
  );
  return record;
}

function assertOperationalLedgerMeta(value: unknown, path: string): JsonRecord {
  const responseName = "operational order response";
  const record = assertOperationalPresentation(value, responseName, path);
  assertFields(record, responseName, path, [
    ["id", "a string", isString],
    ["client_id", "a string", isString],
    ["store_id", "a string", isString],
    ["store_name", "a string", isString],
    ["client_name", "a string", isString],
    ["quotation_id", "a string", isString],
    ["quotation_date", "a string", isString],
    ["status", "a string", isString],
    ["version", "a finite number", isNumber],
    ["created_at", "a string", isString],
    ["updated_at", "a string", isString],
  ]);
  assertOptionalField(
    record,
    responseName,
    path,
    "checkin_id",
    "a string or null",
    isStringOrNull,
  );
  assertOptionalField(
    record,
    responseName,
    path,
    "pending_change_request_id",
    "a string",
    isString,
  );
  return record;
}

function assertFinancialEnrichedOrder(value: unknown, path: string): void {
  const responseName = "financial order response";
  const record = assertFinancialLedgerMeta(value, path);
  const returnSummary = asRecord(
    record.return_summary,
    responseName,
    `${path}.return_summary`,
  );
  assertFields(returnSummary, responseName, `${path}.return_summary`, [
    ["decision", "a finite number", isNumber],
    ["substituido", "a finite number", isNumber],
    ["devolvido", "a finite number", isNumber],
  ]);
  assertArray(
    record.items,
    responseName,
    `${path}.items`,
    assertFinancialEnrichedOrderItem,
  );
}

function assertOperationalEnrichedOrder(value: unknown, path: string): void {
  const responseName = "operational order response";
  const record = assertOperationalLedgerMeta(value, path);
  const returnSummary = assertOperationalPresentation(
    record.return_summary,
    responseName,
    `${path}.return_summary`,
  );
  assertFields(returnSummary, responseName, `${path}.return_summary`, [
    ["decision", "a finite number", isNumber],
    ["substituted", "a finite number", isNumber],
    ["returned", "a finite number", isNumber],
  ]);
  assertArray(
    record.items,
    responseName,
    `${path}.items`,
    assertOperationalEnrichedOrderItem,
  );
}

function assertFinancialOrderSummary(value: unknown, path: string): void {
  const responseName = "financial order summary response";
  const record = assertFinancialLedgerMeta(value, path);
  assertFields(record, responseName, path, [
    ["total_price", "a finite number", isNumber],
    ["item_count", "a finite number", isNumber],
  ]);
}

function assertOperationalOrderSummary(value: unknown, path: string): void {
  const responseName = "operational order summary response";
  const record = assertOperationalLedgerMeta(value, path);
  assertFields(record, responseName, path, [
    ["item_count", "a finite number", isNumber],
  ]);
}

function assertFinancialChangeRequest(value: unknown, path: string): void {
  const responseName = "financial change request response";
  const record = asRecord(value, responseName, path);
  assertNoPresentation(record, responseName, path);
  assertFields(record, responseName, path, [
    ["id", "a string", isString],
    ["order_id", "a string", isString],
    ["status", "a string", isString],
    ["requester_id", "a string", isString],
    ["expected_version", "a finite number", isNumber],
    ["created_at", "a string", isString],
    ["updated_at", "a string", isString],
    ["updated_by", "a string", isString],
  ]);
  asRecord(record.changes_payload, responseName, `${path}.changes_payload`);
}

function assertOperationalChangeRequest(value: unknown, path: string): void {
  const responseName = "operational change request response";
  const record = assertOperationalPresentation(value, responseName, path);
  assertFields(record, responseName, path, [
    ["id", "a string", isString],
    ["order_id", "a string", isString],
    ["status", "a string", isString],
    ["requester_id", "a string", isString],
    ["expected_version", "a finite number", isNumber],
    ["created_at", "a string", isString],
    ["updated_at", "a string", isString],
  ]);
  if (
    record.quantity_changes !== undefined &&
    record.quantity_changes !== null
  ) {
    const changes = assertOperationalPresentation(
      record.quantity_changes,
      responseName,
      `${path}.quantity_changes`,
    );
    if (changes.add !== undefined) {
      assertArray(
        changes.add,
        responseName,
        `${path}.quantity_changes.add`,
        (item, itemPath) => {
          const change = assertOperationalPresentation(
            item,
            responseName,
            itemPath,
          );
          assertFields(change, responseName, itemPath, [
            ["quotation_item_id", "a string", isString],
            ["quantity", "a finite number", isNumber],
          ]);
        },
      );
    }
    if (changes.update !== undefined) {
      assertArray(
        changes.update,
        responseName,
        `${path}.quantity_changes.update`,
        (item, itemPath) => {
          const change = assertOperationalPresentation(
            item,
            responseName,
            itemPath,
          );
          assertFields(change, responseName, itemPath, [
            ["order_item_id", "a string", isString],
            ["quantity", "a finite number", isNumber],
          ]);
        },
      );
    }
  }
}

function assertFinancialOrderOverview(value: unknown, path: string): void {
  const responseName = "financial order overview response";
  const record = asRecord(value, responseName, path);
  assertNoPresentation(record, responseName, path);
  assertFields(record, responseName, path, [
    ["date", "a string", isString],
    ["total_quantity", "a finite number", isNumber],
  ]);
  assertArray(
    record.by_store,
    responseName,
    `${path}.by_store`,
    (store, storePath) => {
      const entry = asRecord(store, responseName, storePath);
      assertFields(entry, responseName, storePath, [
        ["store_id", "a string", isString],
        ["store_name", "a string", isString],
        ["total_quantity", "a finite number", isNumber],
      ]);
      assertArray(
        entry.by_supplier,
        responseName,
        `${storePath}.by_supplier`,
        (supplier, supplierPath) => {
          const supplierEntry = asRecord(supplier, responseName, supplierPath);
          assertFields(supplierEntry, responseName, supplierPath, [
            ["supplier_id", "a string", isString],
            ["supplier_name", "a string", isString],
            ["quantity", "a finite number", isNumber],
          ]);
        },
      );
    },
  );
  assertArray(
    record.by_supplier,
    responseName,
    `${path}.by_supplier`,
    (supplier, supplierPath) => {
      const entry = asRecord(supplier, responseName, supplierPath);
      assertFields(entry, responseName, supplierPath, [
        ["supplier_id", "a string", isString],
        ["supplier_name", "a string", isString],
        ["quantity", "a finite number", isNumber],
      ]);
    },
  );
}

function assertOperationalOrderOverview(value: unknown, path: string): void {
  const responseName = "operational order overview response";
  const record = assertOperationalPresentation(value, responseName, path);
  assertFields(record, responseName, path, [
    ["date", "a string", isString],
    ["quantity", "a finite number", isNumber],
  ]);
  assertArray(
    record.by_store,
    responseName,
    `${path}.by_store`,
    (store, storePath) => {
      const entry = assertOperationalPresentation(
        store,
        responseName,
        storePath,
      );
      assertFields(entry, responseName, storePath, [
        ["store_id", "a string", isString],
        ["store_name", "a string", isString],
        ["quantity", "a finite number", isNumber],
      ]);
      assertArray(
        entry.by_supplier,
        responseName,
        `${storePath}.by_supplier`,
        (supplier, supplierPath) => {
          const supplierEntry = assertOperationalPresentation(
            supplier,
            responseName,
            supplierPath,
          );
          assertFields(supplierEntry, responseName, supplierPath, [
            ["supplier_id", "a string", isString],
            ["supplier_name", "a string", isString],
            ["quantity", "a finite number", isNumber],
          ]);
        },
      );
    },
  );
  assertArray(
    record.by_supplier,
    responseName,
    `${path}.by_supplier`,
    (supplier, supplierPath) => {
      const entry = assertOperationalPresentation(
        supplier,
        responseName,
        supplierPath,
      );
      assertFields(entry, responseName, supplierPath, [
        ["supplier_id", "a string", isString],
        ["supplier_name", "a string", isString],
        ["quantity", "a finite number", isNumber],
      ]);
    },
  );
}

function assertFinancialAuditEntry(value: unknown, path: string): void {
  const responseName = "financial order audit response";
  const record = asRecord(value, responseName, path);
  assertNoPresentation(record, responseName, path);
  assertFields(record, responseName, path, [
    ["id", "a finite number", isNumber],
    ["order_id", "a string", isString],
    ["actor_id", "a string", isString],
    ["action", "a string", isString],
    ["created_at", "a string", isString],
  ]);
}

function assertOperationalAuditEntry(value: unknown, path: string): void {
  const responseName = "operational order audit response";
  const record = assertOperationalPresentation(value, responseName, path);
  assertFields(record, responseName, path, [
    ["id", "a finite number", isNumber],
    ["order_id", "a string", isString],
    ["actor_id", "a string", isString],
    ["action", "a string", isString],
    ["created_at", "a string", isString],
  ]);
}

function assertFinancialPage(
  value: unknown,
  responseName: string,
  path: string,
  itemValidator: ResponseValidator,
): void {
  const record = asRecord(value, responseName, path);
  assertNoPresentation(record, responseName, path);
  assertFields(record, responseName, path, [
    ["total", "a finite number", isNumber],
    ["limit", "a finite number", isNumber],
    ["offset", "a finite number", isNumber],
  ]);
  assertArray(record.items, responseName, `${path}.items`, itemValidator);
}

function assertOperationalPage(
  value: unknown,
  responseName: string,
  path: string,
  itemValidator: ResponseValidator,
): void {
  const record = assertOperationalPresentation(value, responseName, path);
  assertFields(record, responseName, path, [
    ["count", "a finite number", isNumber],
    ["limit", "a finite number", isNumber],
    ["offset", "a finite number", isNumber],
  ]);
  assertArray(record.items, responseName, `${path}.items`, itemValidator);
}

function parseOrderEndpointResponse<T>({
  payload,
  financialName,
  operationalName,
  financialValidator,
  operationalValidator,
}: {
  payload: unknown;
  financialName: string;
  operationalName: string;
  financialValidator: ResponseValidator;
  operationalValidator: ResponseValidator;
}): T {
  const record = asRecord(payload, financialName, "$");
  if ("presentation" in record) {
    if (!hasOperationalPresentation(record)) {
      invalidResponse(operationalName, "$.presentation", '"operational"');
    }
    assertNoFinancialKeysInOperationalPayload(record);
    operationalValidator(record, "$");
  } else {
    financialValidator(record, "$");
  }
  return payload as T;
}

export function parseOrderSummaryPageResponse(
  payload: unknown,
): OrderSummaryPageResponse {
  return parseOrderEndpointResponse({
    payload,
    financialName: "financial order summary page response",
    operationalName: "operational order summary page response",
    financialValidator: (value, path) =>
      assertFinancialPage(
        value,
        "financial order summary page response",
        path,
        assertFinancialOrderSummary,
      ),
    operationalValidator: (value, path) =>
      assertOperationalPage(
        value,
        "operational order summary page response",
        path,
        assertOperationalOrderSummary,
      ),
  });
}

export function parseEnrichedOrderResponse(
  payload: unknown,
): EnrichedOrderResponse {
  return parseOrderEndpointResponse({
    payload,
    financialName: "financial order response",
    operationalName: "operational order response",
    financialValidator: assertFinancialEnrichedOrder,
    operationalValidator: assertOperationalEnrichedOrder,
  });
}

export function parseEnrichedOrderPageResponse(
  payload: unknown,
): EnrichedOrderPageResponse {
  return parseOrderEndpointResponse({
    payload,
    financialName: "financial order page response",
    operationalName: "operational order page response",
    financialValidator: (value, path) =>
      assertFinancialPage(
        value,
        "financial order page response",
        path,
        assertFinancialEnrichedOrder,
      ),
    operationalValidator: (value, path) =>
      assertOperationalPage(
        value,
        "operational order page response",
        path,
        assertOperationalEnrichedOrder,
      ),
  });
}

export function parseEnrichedOrderItemResponse(
  payload: unknown,
): EnrichedOrderItemResponse {
  return parseOrderEndpointResponse({
    payload,
    financialName: "financial enriched order item response",
    operationalName: "operational enriched order item response",
    financialValidator: assertFinancialEnrichedOrderItem,
    operationalValidator: assertOperationalEnrichedOrderItem,
  });
}

export function parseOrderResponse(payload: unknown): OrderResponse {
  return parseOrderEndpointResponse({
    payload,
    financialName: "financial order response",
    operationalName: "operational order response",
    financialValidator: assertFinancialOrder,
    operationalValidator: assertOperationalOrder,
  });
}

export function parseChangeRequestResponse(
  payload: unknown,
): ChangeRequestResponse {
  return parseOrderEndpointResponse({
    payload,
    financialName: "financial change request response",
    operationalName: "operational change request response",
    financialValidator: assertFinancialChangeRequest,
    operationalValidator: assertOperationalChangeRequest,
  });
}

export function parseOrderOverviewResponse(
  payload: unknown,
): OrderOverviewResponse {
  return parseOrderEndpointResponse({
    payload,
    financialName: "financial order overview response",
    operationalName: "operational order overview response",
    financialValidator: assertFinancialOrderOverview,
    operationalValidator: assertOperationalOrderOverview,
  });
}

export function parseOrderAuditEntryPageResponse(
  payload: unknown,
): OrderAuditEntryPageResponse {
  return parseOrderEndpointResponse({
    payload,
    financialName: "financial order audit page response",
    operationalName: "operational order audit page response",
    financialValidator: (value, path) =>
      assertFinancialPage(
        value,
        "financial order audit page response",
        path,
        assertFinancialAuditEntry,
      ),
    operationalValidator: (value, path) =>
      assertOperationalPage(
        value,
        "operational order audit page response",
        path,
        assertOperationalAuditEntry,
      ),
  });
}

export function listOrderSummaries(
  limit?: number,
  offset?: number,
  filters?: OrderSummaryFilters,
  signal?: AbortSignal,
): Promise<OrderSummaryPageResponse> {
  const qs = new URLSearchParams();
  if (limit !== undefined) qs.set("limit", String(limit));
  if (offset !== undefined) qs.set("offset", String(offset));
  if (filters?.client_id) qs.set("client_id", filters.client_id);
  if (filters?.store_id) qs.set("store_id", filters.store_id);
  if (filters?.status) qs.append("status", filters.status);
  for (const status of filters?.statuses ?? []) {
    if (status) qs.append("status", status);
  }
  const search = filters?.search?.trim();
  if (search) qs.set("search", search);
  if (filters?.date_from) qs.set("date_from", filters.date_from);
  if (filters?.date_to) qs.set("date_to", filters.date_to);

  const query = qs.toString();
  return apiFetch<unknown>(
    `${ENDPOINTS.orders.summary}${query ? `?${query}` : ""}`,
    { signal },
  ).then(parseOrderSummaryPageResponse);
}

export function getEnrichedOrderDetail(
  id: string,
): Promise<EnrichedOrderResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.enrichedDetail(id)).then(
    parseEnrichedOrderResponse,
  );
}

export function listEnrichedOrders(
  limit = 2000,
  offset = 0,
  params?: { status?: string; search?: string },
): Promise<EnrichedOrderPageResponse> {
  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (params?.status) qs.set("status", params.status);
  if (params?.search) qs.set("search", params.search);
  return apiFetch<unknown>(`${ENDPOINTS.orders.enriched}?${qs}`).then(
    parseEnrichedOrderPageResponse,
  );
}

export function addItemToOrder(
  payload: AddItemToOrderPayload,
): Promise<EnrichedOrderResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.addItem, {
    method: "POST",
    body: JSON.stringify(payload),
  }).then(parseEnrichedOrderResponse);
}

export function updateOrderItem(
  orderId: string,
  itemId: string,
  payload: UpdateOrderItemPayload,
): Promise<EnrichedOrderItemResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.item(orderId, itemId), {
    method: "PATCH",
    body: JSON.stringify(payload),
  }).then(parseEnrichedOrderItemResponse);
}

export function setOrderItemBonus(
  orderId: string,
  itemId: string,
  payload: SetOrderItemBonusPayload,
): Promise<EnrichedOrderItemResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.itemBonus(orderId, itemId), {
    method: "PATCH",
    body: JSON.stringify(payload),
  }).then(parseEnrichedOrderItemResponse);
}

export function replaceOrderItem(
  orderId: string,
  itemId: string,
  payload: ReplaceOrderItemPayload,
): Promise<EnrichedOrderItemResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.replaceItem(orderId, itemId), {
    method: "POST",
    body: JSON.stringify(payload),
  }).then(parseEnrichedOrderItemResponse);
}

export function getLegacyKgMigrationPreview(
  orderId: string,
  itemId: string,
): Promise<LegacyKgMigrationPreview> {
  return apiFetch(ENDPOINTS.orders.legacyKgMigration(orderId, itemId));
}

export function migrateLegacyKgItem(
  orderId: string,
  itemId: string,
  payload: LegacyKgMigrationPayload,
): Promise<EnrichedOrderItem> {
  return apiFetch(ENDPOINTS.orders.legacyKgMigration(orderId, itemId), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function removeOrderItem(
  orderId: string,
  itemId: string,
): Promise<RemoveOrderItemResponse> {
  return apiFetch<RemoveOrderItemResponse | undefined>(
    ENDPOINTS.orders.item(orderId, itemId),
    { method: "DELETE" },
  ).then(
    (response) => response ?? { order_deleted: false, checkin_deleted: false },
  );
}

export function completeOrder(orderId: string): Promise<OrderResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.complete(orderId), {
    method: "POST",
  }).then(parseOrderResponse);
}

export function reopenOrder(orderId: string): Promise<OrderResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.reopen(orderId), {
    method: "POST",
  }).then(parseOrderResponse);
}

export function updateOrder(
  orderId: string,
  payload: { status?: string; obs?: string },
): Promise<OrderResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.detail(orderId), {
    method: "PATCH",
    body: JSON.stringify(payload),
  }).then(parseOrderResponse);
}

export function deleteOrder(orderId: string): Promise<void> {
  return apiFetch(ENDPOINTS.orders.detail(orderId), { method: "DELETE" });
}

export function getChangeRequest(crId: string): Promise<ChangeRequestResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.getChangeRequest(crId)).then(
    parseChangeRequestResponse,
  );
}

export function getChangeRequestItems(
  crId: string,
): Promise<ChangeRequestItem[]> {
  return apiFetch<unknown>(ENDPOINTS.orders.changeRequestItems(crId)).then(
    (payload) => {
      assertArray(
        payload,
        "change request items response",
        "$",
        (value, path) => {
          const item = asRecord(value, "change request item response", path);
          assertFields(item, "change request item response", path, [
            ["id", "a string", isString],
            ["product_name", "a string", isString],
            ["supplier_name", "a string", isString],
            ["unit_type", "a string", isString],
          ]);
        },
      );
      return payload as ChangeRequestItem[];
    },
  );
}

export function createChangeRequest(
  orderId: string,
  payload: CreateChangeRequestPayload,
): Promise<ChangeRequestResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.changeRequest(orderId), {
    method: "POST",
    body: JSON.stringify(payload),
  }).then(parseChangeRequestResponse);
}

export function updateChangeRequest(
  crId: string,
  payload: CreateChangeRequestPayload,
): Promise<ChangeRequestResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.updateChangeRequest(crId), {
    method: "PATCH",
    body: JSON.stringify(payload),
  }).then(parseChangeRequestResponse);
}

export function deleteChangeRequest(crId: string): Promise<void> {
  return apiFetch(ENDPOINTS.orders.deleteChangeRequest(crId), {
    method: "DELETE",
  });
}

export function approveChangeRequest(
  crId: string,
): Promise<ChangeRequestResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.approveChangeRequest(crId), {
    method: "POST",
  }).then(parseChangeRequestResponse);
}

export function rejectChangeRequest(
  crId: string,
  payload: RejectChangeRequestPayload,
): Promise<ChangeRequestResponse> {
  return apiFetch<unknown>(ENDPOINTS.orders.rejectChangeRequest(crId), {
    method: "POST",
    body: JSON.stringify(payload),
  }).then(parseChangeRequestResponse);
}
